// Pure helpers that shape curriculum + progress records for the course player and dashboard.
// The database is the source of truth for what is complete and what is unlocked; these helpers only
// mirror its rules so the UI can show lock icons and "next lesson" without extra round trips.
import type { Chapter, Lesson, LessonProgress } from './types';

export interface OutlineLesson extends Lesson {
  status: 'todo' | 'in_progress' | 'done';
  locked: boolean;
}
export interface OutlineChapter extends Chapter {
  lessons: OutlineLesson[];
  completed: number;
  required: number;
}
export interface Outline {
  chapters: OutlineChapter[];
  ordered: OutlineLesson[];
  percent: number;
  remainingMinutes: number;
  nextLesson: OutlineLesson | null;
}

export function buildOutline(
  chapters: Chapter[],
  lessons: Lesson[],
  progress: LessonProgress[],
  opts: { sequential: boolean; enrolled: boolean; staff?: boolean },
): Outline {
  const prog = new Map(progress.map((p) => [p.lesson_id, p]));
  const sortedChapters = [...chapters].sort((a, b) => a.position - b.position);
  const byChapter = new Map<string, Lesson[]>();
  for (const l of lessons) byChapter.set(l.chapter_id, [...(byChapter.get(l.chapter_id) ?? []), l]);

  const ordered: OutlineLesson[] = [];
  const out: OutlineChapter[] = [];
  let prevRequiredDone = true;

  for (const ch of sortedChapters) {
    const ls = (byChapter.get(ch.id) ?? []).sort((a, b) => a.position - b.position);
    const rows: OutlineLesson[] = [];
    for (const l of ls) {
      const p = prog.get(l.id);
      const status: OutlineLesson['status'] = p?.status === 'completed' ? 'done' : p ? 'in_progress' : 'todo';
      const prereqOk = !l.prerequisite_lesson_id || prog.get(l.prerequisite_lesson_id)?.status === 'completed';
      let locked: boolean;
      if (opts.staff) locked = false;
      else if (!opts.enrolled) locked = !l.is_preview;
      else locked = !prereqOk || (opts.sequential && !prevRequiredDone);
      const row = { ...l, status, locked };
      rows.push(row);
      ordered.push(row);
      if (l.is_required) prevRequiredDone = status === 'done';
    }
    out.push({
      ...ch,
      lessons: rows,
      completed: rows.filter((r) => r.is_required && r.status === 'done').length,
      required: rows.filter((r) => r.is_required).length,
    });
  }

  const required = ordered.filter((l) => l.is_required);
  const done = required.filter((l) => l.status === 'done').length;
  const percent = required.length ? Math.round((done / required.length) * 1000) / 10 : 0;
  const remainingMinutes = ordered.filter((l) => l.status !== 'done').reduce((s, l) => s + (l.est_minutes ?? 0), 0);
  const nextLesson = ordered.find((l) => l.status !== 'done' && !l.locked) ?? null;
  return { chapters: out, ordered, percent, remainingMinutes, nextLesson };
}

export function formatMinutes(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

export function formatPrice(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase(), minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
}

export function neighbours(ordered: OutlineLesson[], lessonId: string) {
  const i = ordered.findIndex((l) => l.id === lessonId);
  return { prev: i > 0 ? ordered[i - 1] : null, next: i >= 0 && i < ordered.length - 1 ? ordered[i + 1] : null, index: i };
}
